import { Link } from 'react-router-dom';
import { Breadcrumbs } from '@/components/site/Breadcrumbs';
import { SiteMeta } from '@/components/site/SiteMeta';
import { findCase } from '@/content/cases';
import { clients, findClient, type Client } from '@/content/clients';
import { sectors } from '@/content/sectors';
import { supportWhatsAppHref } from '@/lib/supportChat';
import { ClientLogo } from '@/pages/site/home/ClientsStrip';

function CaseCard({ slug, chips }: { slug: string; chips: string[] }) {
  const client = findClient(slug) as Client;
  const item = findCase(slug)!;
  return (
    <article className="hx-ccard" id={slug}>
      <div className="who">
        <ClientLogo client={client} size={58} />
        <div>
          <strong>{client.name}</strong>
          <span>{client.segment}</span>
        </div>
      </div>
      <blockquote>&ldquo;{item.highlight}&rdquo;</blockquote>
      <div className="by">
        {item.author.name} · {item.author.role}
      </div>
      <div className="hx-chips2">
        {chips.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <Link className="go" to={`/casos/${slug}`}>
        Ler o caso completo →
      </Link>
    </article>
  );
}

export function ClientesPage() {
  const protvaco = findClient('protvaco') as Client;
  const caso = findCase('protvaco')!;
  const others = clients.filter((c) => !c.href);
  return (
    <>
      <SiteMeta
        title="Clientes da Operax: resultados reais com WhatsApp oficial | Operax"
        description="Quem opera com a Operax: 94% de entrega na cobrança da Protvaço, agenda automática em consultório, jornada de aluno e atendimento de supermercado no WhatsApp."
        path="/clientes"
      />
      <div className="hx-dark">
        <section className="hx-chero">
          <div className="hx-wrap">
            <Breadcrumbs items={[{ name: 'Início', path: '/' }, { name: 'Clientes' }]} />
            <span className="hx-label" style={{ color: '#60a5fa' }}>
              Clientes · dados reais
            </span>
            <h1>Quem opera com a Operax, e o que mudou.</h1>
            <p>Números tirados do painel de cada cliente, com período e método. Sem arredondar para cima.</p>
          </div>
        </section>
        <section className="hx-cbody">
          <div className="hx-wrap">
            <div className="hx-stats">
              <div>
                <b>{clients.length}</b>
                <span>empresas já operando na Operax</span>
              </div>
              <div>
                <b className="g">94%</b>
                <span>de entrega em cobrança (Protvaço, set/2026)</span>
              </div>
              <div>
                <b>{sectors.length}</b>
                <span>setores com pacote pronto</span>
              </div>
              <div>
                <b>100%</b>
                <span>no WhatsApp oficial da Meta</span>
              </div>
            </div>

            <article className="hx-ccard wide" id="protvaco">
              <div className="hx-col">
                <div className="who">
                  <ClientLogo client={protvaco} size={58} />
                  <div>
                    <strong>{protvaco.name}</strong>
                    <span>{protvaco.segment} · Cobrança no WhatsApp</span>
                  </div>
                </div>
                <blockquote>&ldquo;{caso.highlight}&rdquo;</blockquote>
                <div className="by">
                  {caso.author.name} · {caso.author.role}
                </div>
                <p className="txt">
                  Os disparos em massa esbarravam em bloqueios da Meta. Com a cobrança saindo pela API oficial, com
                  modelos aprovados, a entrega subiu quase 7 pontos em um mês.
                </p>
                <Link className="go" to="/casos/protvaco">
                  Ler o caso completo →
                </Link>
              </div>
              <div className="hx-col">
                <div className="hx-tile">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                    <span style={{ color: '#cbd5e1' }}>Cobranças entregues</span>
                    <span className="big">94%</span>
                  </div>
                  <div className="hx-bars">
                    <div>
                      <span style={{ width: 36 }}>Ago</span>
                      <i>
                        <b style={{ width: '87.1%' }} />
                      </i>
                      <span style={{ width: 48, textAlign: 'right' }}>87,1%</span>
                    </div>
                    <div>
                      <span style={{ width: 36 }}>Set</span>
                      <i>
                        <b style={{ width: '93.7%' }} />
                      </i>
                      <span style={{ width: 48, textAlign: 'right', color: '#f1f5f9' }}>93,7%</span>
                    </div>
                  </div>
                </div>
                <div className="hx-tiles2">
                  {caso.metrics.slice(1).map((m, i) => (
                    <div className="hx-tile" key={m.value}>
                      <div className={`big${i === 0 ? ' g' : ''}`}>{m.value}</div>
                      <p>{m.label}</p>
                    </div>
                  ))}
                </div>
                <div className="hx-fine">{caso.method}</div>
              </div>
            </article>

            <div className="hx-cases">
              <CaseCard slug="consultorio-dr-lucas" chips={['Agenda', 'Atendimento automático', 'WhatsApp oficial']} />
              <CaseCard slug="metodo-stop-gastos" chips={['Jornada automática', 'Pesquisa prévia', 'Liberação do app']} />
              <CaseCard slug="supermercado-somar" chips={['Supermercado', 'Atendimento no WhatsApp', 'Em implantação']} />
            </div>

            <div className="hx-wrap" style={{ gap: 16 }}>
              <span className="hx-label">Também operam com a Operax</span>
              <div className="hx-list">
                {others.map((c) => (
                  <div className="hx-client" key={c.slug}>
                    <ClientLogo client={c} />
                    <span className="hx-client-txt">
                      <strong style={{ whiteSpace: 'normal' }}>{c.name}</strong>
                      {c.segment && <span>{c.segment}</span>}
                    </span>
                  </div>
                ))}
              </div>
              <p className="hx-fine">
                Os resultados de cada empresa entram aqui quando ela autoriza a publicação, sempre com período e
                método de medição.
              </p>
            </div>
          </div>
        </section>
      </div>
      <section className="hx-cta-light">
        <div className="hx-wrap">
          <div>
            <h2>O próximo caso pode ser o seu.</h2>
            <p>Monte o plano com os apps da sua operação e comece hoje.</p>
          </div>
          <div className="hx-actions">
            <Link className="btn hx-solid" style={{ padding: '14px 22px' }} to="/#solucoes">
              Ver pacotes por setor
            </Link>
            <a className="btn btn-ghost" href={supportWhatsAppHref()}>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
